import { LexiOptions } from "@civic/lexi";

export enum CredentialSignerType {
    WALLET = 'WALLET',
    PASSKEY = 'PASSKEY',
    ECDH_ES = 'ECDH-ES'
}
export type WalletSigner = {
    type: CredentialSignerType.WALLET;
    signMessage: (message: Uint8Array) => Promise<Uint8Array>;
};

export type PassKeySigner = {
    type: CredentialSignerType.PASSKEY;
    options: {
        rpId: string;
    };
};

export type ECDHESSigner = {
    type: CredentialSignerType.ECDH_ES;
    secretKey: Uint8Array;
};

export function createECDHESSigner(secretKey: Uint8Array): ECDHESSigner {
    return {type: CredentialSignerType.ECDH_ES, secretKey}
};

export enum RecipientKeyType {
    ECDH_ES = 'ECDH-ES'
}
export type ECDHESRecipientKey = {
    type: RecipientKeyType.ECDH_ES,
    kid?: string;
    publicKey: Uint8Array
};
export type RecipientKey = ECDHESRecipientKey;

export function createECDHESRecipientKey(publicKey: Uint8Array, kid?: string): ECDHESRecipientKey {
    return {type: RecipientKeyType.ECDH_ES, kid, publicKey}
};

export type CredentialClientOptions = {
    identityStoreEndpoint?: string;
    auth: string;
    signer?: WalletSigner | PassKeySigner | ECDHESSigner;
    lexi?: LexiOptions;
};

export type ContentFormat = 'JSON' | 'JWE';
export type ContentItem = { 
  name: string;
  mimeType: string;
  format: ContentFormat;
  sha256?: string;
  blob: Uint8Array;
}
export type ContentItemInternal = Omit<ContentItem, 'blob'> & {
    encryptionMetadata?: string | Record<string, unknown>;
    presignedUrl?: string;
    ref?: string;
};

export type ContentItemInternalWithStringBlob = ContentItemInternal & {
    stringBlob: string;
};

export type CredentialMetadata = {
    issuanceDate: string; // mongo will auto-convert these to a date if they are ISO compliant
    expirationDate?: string | null;
};

export type CreateStoreRequest = {
    credentialId: string;
    tags: string[];
    metadata: CredentialMetadata;
    content: ContentItem[];
    storageProvider?: string;
};

export type SparseCredential = Omit<CreateStoreRequest, 'content'>;

export type InternalStoreCredential = Omit<CreateStoreRequest, 'content'> & {
    storageProvider: string;
    content: ContentItemInternal[];
};

export type StoreCredential = Omit<CreateStoreRequest, 'content'> & {
    content: ContentItem[];
}
